import { useEffect, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { checkProductsForMultipleTypes } from '../services/ProductTypeApi';

/**
 * Hook to determine navbar item visibility based on Expert Hub theme
 * 
 * Returns an object with visibility flags for navbar items:
 * - showShop: Boolean indicating if Shop should be visible
 * - showCollections: Boolean indicating if Collections should be visible
 * - showServices: Boolean indicating if Services should be visible
 * - isExpertHub: Boolean indicating if current theme is Expert Hub
 * - loading: Boolean indicating if data is still loading
 */
export const useExpertHubNavigation = () => {
  const { themeConfig } = useTheme();
  const [showShop, setShowShop] = useState(true);
  const [showCollections, setShowCollections] = useState(true);
  const [showServices, setShowServices] = useState(false);
  const [loading, setLoading] = useState(true);

  const isExpertHub = themeConfig?.preset === 'experthub';

  useEffect(() => {
    const loadProductAvailability = async () => {
      try {
        setLoading(true);

        if (!isExpertHub) {
          // Not Expert Hub, show everything by default
          setShowShop(true);
          setShowCollections(true);
          setShowServices(false);
          setLoading(false);
          return;
        }

        // For Expert Hub theme, check product availability
        const productResults = await checkProductsForMultipleTypes([0, 1, 2]);

        const type0HasProducts = productResults[0] || false;
        const type1HasProducts = productResults[1] || false;
        const type2HasProducts = productResults[2] || false;

        // Hide Shop if neither type 0 nor type 1 has products
        const hasShopProducts = type0HasProducts || type1HasProducts;
        setShowShop(hasShopProducts);

        // Always hide Collections for Expert Hub
        setShowCollections(false);

        // Show Services if type 2 has products
        setShowServices(type2HasProducts);

        setLoading(false);
      } catch (error) {
        console.error('Error loading product availability:', error);
        setLoading(false);
        // Default behavior in case of error
        setShowShop(true);
        setShowCollections(true);
        setShowServices(false);
      }
    };

    loadProductAvailability();
  }, [isExpertHub]);

  return {
    showShop,
    showCollections,
    showServices,
    isExpertHub,
    loading,
  };
};
